import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Anchor, ArrowRight, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { Country, City } from "country-state-city";
import { API, fmtErr } from "../lib/api";
import { UNIFIED_SYSTEMS } from "../lib/systems";
import { ComboBox } from "../components/ComboBox";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";

const BLANK = { company_name: "", contact_name: "", email: "", phone: "", country: "", city: "", description: "" };

export default function SupplierApply() {
  const [form, setForm] = useState(BLANK);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const countryNames = useMemo(() => Country.getAllCountries().map((c) => c.name), []);
  const nameToIso = useMemo(() => Object.fromEntries(Country.getAllCountries().map((c) => [c.name, c.isoCode])), []);
  const cityNames = useMemo(() => {
    const iso = nameToIso[form.country];
    if (!iso) return [];
    return [...new Set((City.getCitiesOfCountry(iso) || []).map((c) => c.name))];
  }, [form.country, nameToIso]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleCat = (name) => setCats((c) => (c.includes(name) ? c.filter((x) => x !== name) : [...c, name]));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.company_name || !form.contact_name || !form.email) { setErr("Company, account name and email are required."); return; }
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/marketplace/apply`, { ...form, categories: cats });
      setDone(true);
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center p-8 bg-zinc-50">
        <div className="w-full max-w-md text-center bg-white rounded-2xl border border-zinc-200 p-8" data-testid="apply-success">
          <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
          <h1 className="font-display text-2xl mt-4">Application received</h1>
          <p className="text-sm text-zinc-600 mt-2">
            Thanks, {form.contact_name.split(" ")[0] || "there"}! We've received your application for <b>{form.company_name}</b>.
            Our team will review it and email you at <b>{form.email}</b> once it's approved — you'll then be able to set a password and manage your listing.
          </p>
          <Link to="/login"><Button className="mt-6 bg-ink hover:bg-black" data-testid="apply-back-login">Back to login</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-2xl mx-auto p-6 lg:p-10">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-7 h-7 rounded-md bg-ink text-white grid place-items-center"><Anchor className="h-4 w-4" /></div>
          <span className="font-display text-[17px] font-medium">Seavanto</span>
        </Link>

        <h1 className="font-display text-3xl font-medium tracking-tight">Apply to join the supplier marketplace</h1>
        <p className="text-sm text-zinc-600 mt-2">
          Reach yacht owners, captains and managers across the Med. Tell us about your company — our team reviews every application before approval.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5 bg-white rounded-2xl border border-zinc-200 p-6" data-testid="apply-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company">Company name *</Label>
              <Input id="company" value={form.company_name} onChange={set("company_name")} required data-testid="apply-company" />
            </div>
            <div>
              <Label htmlFor="contact">Account Name *</Label>
              <Input id="contact" value={form.contact_name} onChange={set("contact_name")} required data-testid="apply-contact" />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={form.email} onChange={set("email")} required autoCapitalize="none" data-testid="apply-email" />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={form.phone} onChange={set("phone")} data-testid="apply-phone" />
            </div>
            <div>
              <Label>Country</Label>
              <ComboBox value={form.country} onChange={(v) => setForm((f) => ({ ...f, country: v, city: "" }))} options={countryNames} placeholder="Select country" testid="apply-country" />
            </div>
            <div>
              <Label>City</Label>
              <ComboBox value={form.city} onChange={(v) => setForm((f) => ({ ...f, city: v }))} options={cityNames} placeholder={form.country ? "Select or add city" : "Type a city"} testid="apply-city" />
            </div>
          </div>

          <div>
            <Label>What do you service? <span className="text-zinc-400 font-normal">(select all that apply)</span></Label>
            <div className="mt-2 flex flex-wrap gap-1.5 max-h-44 overflow-y-auto p-1" data-testid="apply-categories">
              {UNIFIED_SYSTEMS.map((name) => (
                <button type="button" key={name} onClick={() => toggleCat(name)}
                  className="focus:outline-none" data-testid={`apply-cat-${name}`}>
                  <Badge variant={cats.includes(name) ? "default" : "outline"}
                    className={cats.includes(name) ? "bg-ink hover:bg-black cursor-pointer" : "cursor-pointer hover:bg-zinc-100 font-normal"}>
                    {name}
                  </Badge>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="desc">Tell us about your company</Label>
            <Textarea id="desc" rows={4} value={form.description} onChange={set("description")}
              placeholder="Services offered, areas covered, experience, certifications…" data-testid="apply-description" />
          </div>

          {err && <div role="alert" className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2" data-testid="apply-error">{err}</div>}

          <Button type="submit" disabled={busy} className="w-full bg-ink hover:bg-black" data-testid="apply-submit">
            {busy ? "Submitting…" : "Submit application"} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <p className="text-xs text-zinc-500 text-center">
            Already approved? <Link to="/login" className="text-ink underline">Log in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
